<template>
  <div class="flex flex-col gap-5">

    <div>
      <b-label class="mb-2 block">{{ t('modal_create_mission_step2.market_segment_label') }}</b-label>
      <b-checkbox-group v-model="selectedMarketSegments" data-e2e="experience-market-segments-group">
        <div class="flex flex-wrap gap-2">
          <b-checkbox-chip v-for="(marketSegment, index) in marketSegmentsItems" :key="marketSegment.value" :value="marketSegment.value" :data-e2e="`experience-market-segment-option-${index}`">
            {{ marketSegment.label }}
          </b-checkbox-chip>
        </div>
      </b-checkbox-group>
    </div>

    <b-chip-input searchable :items="personasItems" v-model="selectedPersonas" :label="t('modal_create_mission_step2.main_persona_targeted')" :max-items="3" data-e2e="experience-personas-input" />

    <div>
      <b-label class="mb-2 block">{{ t('modal_create_mission_step2.market_segment_deal_size') }}</b-label>
      <b-checkbox-group v-model="selectedDealSizes" data-e2e="experience-deal-sizes-group">
        <div class="flex flex-wrap gap-2">
          <b-checkbox-chip v-for="(dealSize, index) in dealSizeItems" :key="dealSize.value" :value="dealSize.value" :data-e2e="`experience-deal-size-option-${index}`">
            {{ dealSize.label }}
          </b-checkbox-chip>
        </div>
      </b-checkbox-group>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRefs } from 'vue';
import BChipInput from "~/components/forms/chip-input/BChipInput.vue";
import {groupByCategory} from "~/utils/categoryGrouping";
import BCheckboxGroup from "~/components/forms/checkbox/BCheckboxGroup.vue";
import BCheckboxChip from "~/components/forms/checkbox/BCheckboxChip.vue";
import BLabel from "~/components/forms/BLabel.vue";
import type { Experience } from "~/api/v2/experiences/ExperienceRepository";
const { t } = useI18n();

interface Props {
  experience?: Experience;
  initialData?: Record<string, any>;
}

const props = defineProps<Props>()
const { initialData } = toRefs(props)

const emit = defineEmits(['change'])

const dealSizeStore = useDealSizeStore()
const personaStore = usePersonaStore()
const marketSegmentStore = useMarketSegmentStore()

const { dealSizes } = storeToRefs(dealSizeStore)
const { marketSegments } = storeToRefs(marketSegmentStore)
const { personas } = storeToRefs(personaStore)

const selectedDealSizes = ref([])
const selectedPersonas = ref([])
const selectedMarketSegments = ref([])

// Flag to prevent emitting during draft restoration
const isRestoringData = ref(false)

const dealSizeItems = computed(() => toSelectOptions(dealSizes.value || [], 'K€'))
const personasItems = computed(() => groupByCategory(personas.value || []))
const marketSegmentsItems = computed(() => toSelectOptions(marketSegments.value || [], t('modal_create_mission.employee_label')))

watch(() => props.experience, (experience) => {
  if (experience) {
    selectedMarketSegments.value = (experience.market_segments || []).map((segment) => segment.market_segment_id)
    selectedPersonas.value = (experience.personas || []).map((persona) => ({ value: persona.persona_id, label: persona.name }))
    selectedDealSizes.value = (experience.deal_sizes || []).map((dealSize) => dealSize.deal_size_id)
  }
}, { immediate: true, deep: true })

// Watch for draft restoration
watch(initialData, (data) => {
  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true
    if (data.market_segment_ids && data.market_segment_ids.length > 0) {
      selectedMarketSegments.value = [...data.market_segment_ids]
    }
    if (data.deal_size_ids && data.deal_size_ids.length > 0) {
      selectedDealSizes.value = [...data.deal_size_ids]
    }
    if (data.persona_ids && data.persona_ids.length > 0) {
      if (data.persona_labels && data.persona_labels.length > 0) {
        selectedPersonas.value = [...data.persona_labels]
      } else {
        // Fallback: use IDs as labels
        selectedPersonas.value = data.persona_ids.map(id => ({ value: id, label: id }))
      }
    }
    // Reset flag after Vue processes all updates
    nextTick(() => {
      isRestoringData.value = false
    })
  }
}, { immediate: true, deep: true })

onMounted(() => {
  dealSizeStore.fetchDealSizes()
  personaStore.fetchPersonas()
  marketSegmentStore.fetchMarketSegments()
})

watch([
  selectedDealSizes,
  selectedPersonas,
  selectedMarketSegments,
], () => {
  // Skip emitting during draft restoration to prevent infinite loops
  if (isRestoringData.value) return

  emit('change', {
    deal_size_ids: selectedDealSizes.value,
    persona_ids: selectedPersonas.value.map((_) => _.value),
    persona_labels: selectedPersonas.value.map((_) => ({ value: _.value, label: _.label })),
    market_segment_ids: selectedMarketSegments.value,
  })
}, { deep: true })
</script>
